import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

export type ExerciseThemeRow = {
  theme_id: string;
  is_primary: boolean;
  theme: Pick<
    Theme,
    'id' | 'slug' | 'title' | 'short_description' | 'illustration_url' | 'visibility' | 'status'
  >;
};

const KEY = (exerciseId: string) => ['exercise-themes', exerciseId] as const;

export function useExerciseThemes(exerciseId: string) {
  return useQuery({
    queryKey: KEY(exerciseId),
    enabled: !!exerciseId,
    queryFn: async (): Promise<ExerciseThemeRow[]> => {
      const { data, error } = await supabase
        .from('exercise_themes')
        .select(
          'theme_id, is_primary, theme:themes(id, slug, title, short_description, illustration_url, visibility, status)',
        )
        .eq('exercise_id', exerciseId);
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as ExerciseThemeRow[]).filter((r) => r.theme);
    },
  });
}

export function useAddExerciseTheme(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (themeId: string) => {
      const { error } = await supabase
        .from('exercise_themes')
        .insert({ exercise_id: exerciseId, theme_id: themeId, is_primary: false });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(exerciseId) }),
  });
}

export function useRemoveExerciseTheme(exerciseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (themeId: string) => {
      const { error } = await supabase
        .from('exercise_themes')
        .delete()
        .eq('exercise_id', exerciseId)
        .eq('theme_id', themeId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(exerciseId) }),
  });
}
